import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "https://app.theastrofate.com";
const DEFAULT_OG = "/favicon.svg";

/**
 * Lightweight SEO helper — sets <title>, meta description, canonical URL, OG tags,
 * Twitter Card tags and an optional JSON-LD payload. No external deps.
 * Usage: <SEO title="..." description="..." schema={{...}} />
 */
export default function SEO({
  title = "AstroFate — Vedic AI Astrology, Kundli & Live Astrologer Consultations",
  description = "Personalized Vedic Kundli, AI astrology guidance, and live astrologer consultations. Discover your true path with AstroFate.",
  image = DEFAULT_OG,
  schema = null,
  noindex = false,
}) {
  const { pathname } = useLocation();
  const url = SITE + pathname;

  useEffect(() => {
    document.title = title;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
      return el;
    };
    const setLink = (rel, href) => {
      // Remove ALL pre-existing links with this rel to avoid duplicates
      document.head.querySelectorAll(`link[rel="${rel}"]`).forEach(n => {
        if (!n.hasAttribute("data-seo")) n.remove();
      });
      let el = document.head.querySelector(`link[rel="${rel}"][data-seo]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute("data-seo", "1");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large" });
    setLink("canonical", url);

    // OpenGraph
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: url });
    setMeta('meta[property="og:image"]', { property: "og:image", content: SITE + image });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "AstroFate" });

    // Twitter
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: SITE + image });

    // JSON-LD schema (replaced per page)
    let ld = document.head.querySelector('script[type="application/ld+json"][data-seo]');
    if (schema) {
      if (!ld) {
        ld = document.createElement("script");
        ld.setAttribute("type", "application/ld+json");
        ld.setAttribute("data-seo", "1");
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify(schema);
    } else if (ld) {
      ld.remove();
    }
  }, [title, description, image, url, noindex, schema]);

  return null;
}

// Common schemas for reuse
export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AstroFate",
  url: SITE,
  logo: SITE + "/logo-badge.png",
  founder: {
    "@type": "Person",
    name: "AstroFate Founder",
    description: "IIT Kharagpur graduate with 5 years of classical Vedic astrology practice",
    alumniOf: { "@type": "CollegeOrUniversity", name: "Indian Institute of Technology Kharagpur" },
  },
  sameAs: ["https://www.theastrofate.com"],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "AstroFate",
  url: SITE,
  potentialAction: {
    "@type": "SearchAction",
    target: SITE + "/blog?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};
